import React from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import { defaults, pick } from 'lodash-es';
import StyledContainedButton from '../../Styled/StyledContainedButton';
import Checkbox from '@ui/Checkbox';
import useUserProfile from '@/hooks/useUserProfile';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { cx } from 'cva';

const schema = yup.object().shape({
  newPosts: yup
    .boolean()
    .oneOf([true, false], 'Get notified when a member creates a new post')
    .notRequired(),
  commentsOnYourPosts: yup
    .boolean()
    .oneOf(
      [true, false],
      `Get notified when a member comments on a post you've created`,
    )
    .notRequired(),
  commentsOnAPostYouHaveCommentedOn: yup
    .boolean()
    .oneOf(
      [true, false],
      `Get notified when a member comments on a post you've commented on`,
    )
    .notRequired(),
  postReactions: yup
    .boolean()
    .oneOf([true, false], 'Get a daily digest of reactions to your posts')
    .notRequired(),
  youAreMentionedOnAPost: yup
    .boolean()
    .oneOf([true, false], 'Get notified when a memeber mentions you on a post')
    .notRequired(),
  newFollower: yup
    .boolean()
    .oneOf([true, false], 'Get notified when a member starts following you')
    .notRequired(),
  communityDigestEmail: yup
    .boolean()
    .oneOf(
      [true, false],
      `Receive a weekly email with top posts from communuities you've joined`,
    )
    .notRequired(),
  newsletterEmail: yup
    .boolean()
    .oneOf([true, false], `Receive a monthly newsletter email`)
    .notRequired(),
});

function EditProfileNotificationsStep(props) {
  const { userProfile, onSubmit, onBack, formData, onNext, saving } = props;

  const isSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('md'));

  const {
    newPosts,
    commentsOnYourPosts,
    commentsOnAPostYouHaveCommentedOn,
    postReactions,
    youAreMentionedOnAPost,
    newFollower,
    communityDigestEmail,
    newsletterEmail,
  } = userProfile?.notificationPreferences || {};

  const defaultValues = defaults(
    pick(formData.notificationPreferences, [
      'newPosts',
      'commentsOnYourPosts',
      'commentsOnAPostYouHaveCommentedOn',
      'postReactions',
      'youAreMentionedOnAPost',
      'newFollower',
      'communityDigestEmail',
      'newsletterEmail',
    ]),
    {
      newPosts: newPosts,
      commentsOnYourPosts: commentsOnYourPosts,
      commentsOnAPostYouHaveCommentedOn: commentsOnAPostYouHaveCommentedOn,
      postReactions: postReactions,
      youAreMentionedOnAPost: youAreMentionedOnAPost,
      newFollower: newFollower,
      communityDigestEmail: communityDigestEmail,
      newsletterEmail: newsletterEmail,
    },
  );

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
  } = form;

  const handleFormSubmit = async (data) => {
    const payload = {
      notificationPreferences: {
        ...data,
      },
    };
    onSubmit(payload, 'Notifications');
  };

  //   console.log('defaultValues', defaultValues);

  return (
    <div
      className={cx(
        'flex w-full flex-1 flex-col gap-12',
        isSmallScreen && 'mb-12',
      )}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-2">
        <div className="flex items-center justify-start gap-4">
          <Controller
            name="newPosts"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.newPosts}
                error={errors.newPosts?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">New posts</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get notified when a member creates a new post
            </Typography>
          </div>
        </div>

        <div className="flex items-center justify-start gap-4">
          <Controller
            name="commentsOnYourPosts"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.commentsOnYourPosts}
                error={errors.commentsOnYourPosts?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">Comments on your posts</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get notified when a member comments on a post you&apos;ve created
            </Typography>
          </div>
        </div>

        <div className="flex items-center justify-start gap-4">
          <Controller
            name="commentsOnAPostYouHaveCommentedOn"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.commentsOnAPostYouHaveCommentedOn}
                error={errors.commentsOnAPostYouHaveCommentedOn?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">
              Comments on a post you&apos;ve commented on
            </Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get notified when a member comments on a post you&apos;ve
              commented on
            </Typography>
          </div>
        </div>

        {/* <div className="flex items-center justify-start gap-4">
          <Controller
            name="postReactions"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.postReactions}
                error={errors.postReactions?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">Post reactions</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get a daily digest of reactions to your posts
            </Typography>
          </div>
        </div> */}

        <div className="flex items-center justify-start gap-4">
          <Controller
            name="youAreMentionedOnAPost"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.youAreMentionedOnAPost}
                error={errors.youAreMentionedOnAPost?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">
              You&apos;re mentioned on a post
            </Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get notified when a member mentions you on a post
            </Typography>
          </div>
        </div>

        <div className="flex items-center justify-start gap-4">
          <Controller
            name="newFollower"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.newFollower}
                error={errors.newFollower?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">New follower</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Get notified when a member starts following you
            </Typography>
          </div>
        </div>

        <div className="flex items-center justify-start gap-4">
          <Controller
            name="communityDigestEmail"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.communityDigestEmail}
                error={errors.communityDigestEmail?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">Community digest Email</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Receive a weekly email with top posts from communuities
              you&apos;ve joined{' '}
            </Typography>
          </div>
        </div>
        <div className="flex items-center justify-start gap-4">
          <Controller
            name="newsletterEmail"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Checkbox
                {...rest}
                defaultChecked={defaultValues.newsletterEmail}
                error={errors.newsletterEmail?.message}
                className="h-7 w-7 border-text-disabled font-extrabold"
              />
            )}
          />
          <div>
            <Typography variant="subtitle1">Newsletter Email</Typography>
            <Typography
              variant="body1"
              color="textDisabled"
              className="font-black">
              Receive a monthly newsletter email
            </Typography>
          </div>
        </div>
        <div className="flex-rows mb-4 flex w-full justify-end gap-4">
          {isValid ? (
            <div className="flex-flow-col">
              <StyledContainedButton
                variant="filled"
                color="primary"
                type="submit"
                className="mt-2"
                loading={saving}>
                Save
              </StyledContainedButton>
            </div>
          ) : (
            <div className="flex-flow-col">
              <StyledContainedButton
                disabled={true}
                variant="outlined"
                color="primary"
                type="submit"
                className="mt-2"
                loading={saving}>
                Save
              </StyledContainedButton>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default EditProfileNotificationsStep;
